import { Injectable } from '@angular/core';
import { ValidatorFn, Validators } from '@angular/forms';

import {
  emailValidator,
  IncludesExactlyValidationError,
  includesExactlyValidator,
  IncludesExactlyValidatorArgs,
  PatternValidationError,
  patternValidator,
  PatternValidatorArgs,
  requiresAny,
  transformValidationErrors,
} from '@fmnts/common/forms';
import { hasProperty, isDefined, isRecord } from '@fmnts/core';
import {
  DeclarativeForm,
  FieldDefinition,
  FormInputField,
  isFormInputField,
  IValidationService,
  PrepareFieldValidationOptions,
  ValidatorConfig,
} from '@fmnts/declarative-forms/core';
import { InlineTranslateable, isInlineTranslatable } from '@fmnts/i18n';

const enum CommonValidatorNames {
  Any = 'any',
  Email = 'email',
  IncludesExactly = 'includesExactly',
  /**
   * Specifies a validator that validates the length of an input,
   * regardless if it is a string or an array.
   */
  Length = 'length',
  Pattern = 'pattern',
  /**
   * Specifies a validator that validates if the field has any value.
   */
  Required = 'required',
}

interface AnyValidatorArgs {
  other_fields?: string[];
}

interface LengthValidatorArgs {
  /** Minimum length of the input */
  min?: number;
  /** Maximum length of the input */
  max?: number;
}

interface LengthValidationError {
  requiredLength: number;
  actualLength: number;
}

type ValidationErrorWithCustomMessage<T> = T & {
  /**
   * Optional validation message
   */
  message?: InlineTranslateable;
};

/**
 * Type guard for checking a "conditionalMin" validation error
 *
 * @param e Errors object
 *
 * @returns
 * `true` if the passed errors object has "conditionalMin"
 * validation errors
 */
export const hasMinLengthError = hasProperty(
  'minlength',
  (u): u is LengthValidationError => isRecord(u),
);

export const hasMaxLengthError = hasProperty(
  'maxlength',
  (u): u is LengthValidationError => isRecord(u),
);

export const hasRequiredError = hasProperty('required', isDefined);

/**
 * Type guard to check if a validation error has a custom `message` object.
 */
export const hasCustomValidationMessage = hasProperty(
  'message',
  isInlineTranslatable,
);

/**
 * Service for creating common validator functions from validator declarations.
 */
@Injectable({
  providedIn: 'root',
})
export class CommonValidationBuilder implements IValidationService {
  buildValidator(
    validator: ValidatorConfig<any>,
    field: FieldDefinition,
  ): ValidatorFn[] {
    switch (validator.name) {
      case CommonValidatorNames.Any:
        return this.buildAnyValidator(
          validator as ValidatorConfig<AnyValidatorArgs>,
          field,
        );
      case CommonValidatorNames.Email:
        return [emailValidator()];
      case CommonValidatorNames.IncludesExactly:
        return this.buildIncludesExactlyValidations(
          validator as ValidatorConfig<IncludesExactlyValidatorArgs>,
        );
      case CommonValidatorNames.Length:
        return this.buildLengthValidations(
          validator as ValidatorConfig<LengthValidatorArgs>,
        );
      case CommonValidatorNames.Pattern:
        return this.buildPatternValidations(
          validator as ValidatorConfig<PatternValidatorArgs>,
        );
      case CommonValidatorNames.Required:
        return this.buildRequiredValidations(
          validator as ValidatorConfig<never>,
        );
    }

    return [];
  }

  prepareField(
    options: PrepareFieldValidationOptions,
  ): FormInputField | undefined {
    const { field } = options;

    if (!isFormInputField(field)) {
      return undefined;
    }

    return field;
  }

  private buildAnyValidator(
    validator: ValidatorConfig<AnyValidatorArgs>,
    field: FieldDefinition,
  ): ValidatorFn[] {
    const otherFieldKeys = validator.args.other_fields || [];
    const otherFieldsPaths = (validator.args.other_fields || []).map((key) => [
      key,
    ]);
    const fieldPaths = [[field.key], ...otherFieldsPaths];

    return [
      requiresAny(fieldPaths, (ctrl) => {
        const rootForm = ctrl.root;
        if (!(rootForm instanceof DeclarativeForm)) {
          return [];
        }

        return otherFieldKeys.flatMap((key) => rootForm.getControlsByKey(key));
      }),
    ];
  }

  private buildIncludesExactlyValidations(
    validator: ValidatorConfig<IncludesExactlyValidatorArgs>,
  ): ValidatorFn[] {
    const includesExactlyValidatorFn = includesExactlyValidator(validator.args);

    if (!validator.messages) {
      return [includesExactlyValidatorFn];
    }

    // Append the message to be consumed within the error-messag pipe
    return [
      transformValidationErrors(
        includesExactlyValidatorFn,
        ({
          includesExactly,
        }: Record<'includesExactly', IncludesExactlyValidationError>): Record<
          'includesExactly',
          ValidationErrorWithCustomMessage<IncludesExactlyValidationError>
        > => ({
          includesExactly: {
            ...includesExactly,
            message: validator.messages?.includesExactly,
          },
        }),
      ),
    ];
  }

  private buildLengthValidations(
    validator: ValidatorConfig<LengthValidatorArgs>,
  ): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (validator.args.min) {
      validators.push(Validators.minLength(validator.args.min));
    }

    if (validator.args.max) {
      validators.push(Validators.maxLength(validator.args.max));
    }

    return validators;
  }

  private buildPatternValidations(
    validator: ValidatorConfig<PatternValidatorArgs>,
  ): ValidatorFn[] {
    const patternValidatorFn = patternValidator(validator.args);

    if (!validator.messages) {
      return [patternValidatorFn];
    }

    // Append the message to be consumed within the error-messag pipe
    return [
      transformValidationErrors(
        patternValidatorFn,
        ({
          pattern,
        }: Record<'pattern', PatternValidationError>): Record<
          'pattern',
          ValidationErrorWithCustomMessage<PatternValidationError>
        > => ({
          pattern: {
            ...pattern,
            message: validator.messages?.pattern,
          },
        }),
      ),
    ];
  }

  private buildRequiredValidations(validator: ValidatorConfig<never>) {
    return [Validators.required];
  }
}
